import React, { useState, useEffect, useCallback } from 'react';
import { BottomSheet, SessionListActivity } from '../../../shared/ui';
import { sitePaymentAllocationV2Api, PaymentAllocationEntryV2 } from '../../../api/sitePaymentAllocationV2Api';
import { paymentsV2Api } from '../../../api/paymentsV2Api';
import PaymentDetailsBottomSheet from '../../paymentsV2/components/PaymentDetailsBottomSheet';
import './ActivityPaymentsBottomSheet.css';

interface ActivityPaymentsBottomSheetProps {
  isOpen: boolean;
  activity: SessionListActivity | null;
  siteId: number;
  onClose: () => void;
}

const ActivityPaymentsBottomSheet: React.FC<ActivityPaymentsBottomSheetProps> = ({
  isOpen,
  activity,
  siteId,
  onClose
}) => {
  const [allocations, setAllocations] = useState<PaymentAllocationEntryV2[]>([]);
  const [isLoadingAllocations, setIsLoadingAllocations] = useState(false);
  const [allocationError, setAllocationError] = useState<string | null>(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentDates, setPaymentDates] = useState<Map<number, string>>(new Map());

  const loadAllocations = useCallback(async () => {
    if (!activity?.id || !siteId) return;

    setIsLoadingAllocations(true);
    setAllocationError(null);
    try {
      const data = await sitePaymentAllocationV2Api.getActivityAllocations(siteId, activity.id);
      setAllocations(data);
      
      // Загружаем даты платежей (оптимизированно - один запрос для всех платежей)
      const paymentIds = data
        .map(a => a.paymentId)
        .filter((id): id is number => id !== undefined && id > 0);
      
      if (paymentIds.length > 0) {
        try {
          // Загружаем все платежи объекта и фильтруем нужные
          const payments = await paymentsV2Api.getSitePayments(siteId, {});
          const datesMap = new Map<number, string>();
          paymentIds.forEach(paymentId => {
            const payment = payments.find(p => p.id === paymentId);
            if (payment) {
              datesMap.set(paymentId, payment.date);
            }
          });
          setPaymentDates(datesMap);
        } catch (err) {
          console.error('Ошибка загрузки дат платежей:', err);
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке распределений:', error);
      setAllocationError('Не удалось загрузить распределения');
    } finally {
      setIsLoadingAllocations(false);
    }
  }, [activity?.id, siteId]);

  useEffect(() => {
    if (isOpen && activity?.id) {
      loadAllocations();
    } else {
      setAllocations([]);
      setAllocationError(null);
    }
  }, [isOpen, activity?.id, loadAllocations]);

  const handlePaymentClick = useCallback((paymentId?: number) => {
    if (!paymentId) return;
    setSelectedPaymentId(paymentId);
    setShowPaymentDetails(true);
  }, []);

  const handleClosePaymentDetails = useCallback(() => {
    setShowPaymentDetails(false);
    setSelectedPaymentId(null);
  }, []);

  const formatAmount = useCallback((amount?: number) => {
    if (amount === undefined || amount === null) return '—';
    return `${amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  const formatTime = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Открываем sheet сразу по isOpen; контент грузится и догружается после открытия
  if (!isOpen) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Информация о платежах"
      size="medium"
      showHandle={true}
      allowSwipeToClose={true}
      allowSwipeToExpand={true}
    >
      <div className="activity-payments">
        {!activity ? (
          <div className="empty-state">
            <i className="ri-loader-4-line ri-spin"></i>
            <p>Загрузка информации об активности...</p>
          </div>
        ) : (
          <>
            {/* Информация об активности */}
            <div className="activity-payments-info">
              <div className="info-row">
                <span className="label">Вид работ:</span>
                <span className="value">{activity.typeOfWorkName || 'Неизвестно'}</span>
              </div>
              {activity.timeStart && (
                <div className="info-row">
                  <span className="label">Дата:</span>
                  <span className="value">{formatDate(activity.timeStart)}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">{activity.timeStart ? 'Время:' : 'Часы:'}</span>
                <span className="value">
                  {activity.timeStart
                    ? `${formatTime(activity.timeStart)}${activity.timeEnd ? ` - ${formatTime(activity.timeEnd)}` : ''}`
                    : `${(activity.hours ?? activity.allocatedHours ?? 0).toFixed(1)} ч`
                  }
                </span>
              </div>
              {activity.rate && (
                <div className="info-row">
                  <span className="label">Ставка:</span>
                  <span className="value">{formatAmount(activity.rate)}/ч</span>
                </div>
              )}
              {activity.comment !== undefined && activity.comment !== null && (
                <div className="info-row">
                  <span className="label">Комментарий:</span>
                  <span className="value">{activity.comment || '(пусто)'}</span>
                </div>
              )}
            </div>

            {/* Финансовая информация */}
            <div className="activity-payments-financial">
              <h3>Финансы</h3>
              
              {activity.amount !== undefined && activity.amount !== null && (
                <div className="financial-row">
                  <span className="label">Наработано:</span>
                  <span className="value amount">{formatAmount(activity.amount)}</span>
                </div>
              )}
              
              {activity.paidAmount !== undefined && activity.paidAmount !== null && activity.paidAmount > 0 && (
                <div className="financial-row">
                  <span className="label">Выплачено:</span>
                  <span className="value paid">{formatAmount(activity.paidAmount)}</span>
                </div>
              )}
              
              {activity.remainingAmount !== undefined && activity.remainingAmount !== null && activity.remainingAmount > 0 && (
                <div className="financial-row">
                  <span className="label">Остаток:</span>
                  <span className="value remaining">{formatAmount(activity.remainingAmount)}</span>
                </div>
              )}
            </div>

            {/* Список распределённых платежей */}
            <div className="activity-payments-list">
              <h3>Распределённые платежи</h3>
              
              {isLoadingAllocations ? (
                <div className="empty-state">
                  <i className="ri-loader-4-line ri-spin"></i>
                  <p>Загрузка распределений...</p>
                </div>
              ) : allocationError ? (
                <div className="empty-state">
                  <i className="ri-error-warning-line"></i>
                  <p>{allocationError}</p>
                </div>
              ) : allocations.length === 0 ? (
                <div className="empty-state">
                  <i className="ri-inbox-line"></i>
                  <p>Нет распределённых платежей для этой активности</p>
                </div>
              ) : (
                <div className="allocations-list">
                  {allocations.map((allocation) => (
                    <div key={allocation.allocationId} className="allocation-item">
                      <div className="allocation-header">
                        <div className="allocation-info">
                          <div className="allocation-amount">{formatAmount(allocation.allocationAmount)}</div>
                          {allocation.paymentLineType && (
                            <div className="allocation-type">{allocation.paymentLineType}</div>
                          )}
                          {allocation.paymentLineDescription && (
                            <div className="allocation-description">{allocation.paymentLineDescription}</div>
                          )}
                          <div className="allocation-date">
                            {formatDate(allocation.allocationDate)}
                          </div>
                        </div>
                      </div>
                      {allocation.paymentId && (
                        <button
                          className="payment-link-button"
                          onClick={() => handlePaymentClick(allocation.paymentId)}
                          title="Открыть детали платежа"
                        >
                          <i className="ri-information-line"></i>
                          <span>Подробнее о платеже</span>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* BottomSheet с деталями платежа */}
      {selectedPaymentId && activity && (
        <PaymentDetailsBottomSheet
          isOpen={showPaymentDetails}
          paymentId={selectedPaymentId}
          siteId={siteId}
          currentActivityId={activity.id}
          onClose={handleClosePaymentDetails}
        />
      )}
    </BottomSheet>
  );
};

export default ActivityPaymentsBottomSheet;










